import React, { useContext, useEffect, useState } from "react";
import { CiEraser } from "react-icons/ci";
import { IoArrowBack } from "react-icons/io5";
import { FormattedMessage, IntlProvider, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { AppDispatch, RootState } from "../../../config/redux";
import {
  ConditionMethodEnum,
  FieldInputTypeEnum,
  FieldTypeEnum,
} from "../../../enum/FormConfigEnum";
import enMessages from "../../../i18n/en.json";
import { AuthContext } from "../../../routes/AuthProvider";
import { globalLoadingDispatch } from "../../../utils/DispatchEvent";
import { isDefined, isNonEmptyString } from "../../../utils/ValidationHelp";
import {
  IAttachmentSetting,
  IDropdownSetting,
  IGetFormConfig,
  IInputFieldSetting,
} from "../models/response/IGetFormResponse";
import { clearQuotation } from "../redux/quoteSlice";
import {
  formPostThunk,
  getFormThunk,
  getQuoteThunk,
} from "../redux/quoteThunks";
import Checkbox from "./Checkbox";
import CongratulationsPage from "./CongratulationsPage";
import { QuotationPreview } from "./QuotationPreview";
import { PreviewAttachment, PreviewDropdown, PreviewInput } from "./QuoteField";
import { TermAndCondition } from "./TermAndCondition";

const ProgressIndicator = ({ value, max }: { value: number; max: number }) => {
  const progress =
    value === 0 && max === 0 ? 100 : Math.min(100, (value / max) * 100); // Calculate percentage, cap at 100%

  return (
    <div className="relative w-full h-4 bg-gray-300 rounded-full shadow-inner">
      <div
        className="h-4 rounded-full shadow bg-gradient-to-r from-blue-500 to-teal-400"
        style={{ width: `${progress}%`, transition: "width 0.5s ease-in-out" }}
      >
        {/* Text percentage display */}
        <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-white">
          {`${Math.round(progress)}%`}
        </div>
      </div>
    </div>
  );
};

export interface IConfigWithValue extends IGetFormConfig {
  value?: string | number;
  file?: Array<File>;
}

export const Quote = ({
  setIsShow,
  isShow,
  isShowInvoice,
  isQuoteOnly,
}: {
  setIsShow: React.Dispatch<React.SetStateAction<boolean>>;
  isShow: boolean;
  isShowInvoice?: boolean;
  isQuoteOnly?: boolean;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intl = useIntl();
  let locale = intl.locale as "en" | "zh" | "ms";

  const dispatch = useDispatch<AppDispatch>();
  const [pageIndex, setPageIndex] = useState(0);
  const [isCheck, setIsCheck] = useState(false);
  const [isShowTnC, setIsShowTnC] = useState(false);
  const [pageBreadrumb, setPageBreadCrumb] = useState<Array<number>>([]);
  const [formConfigWithValue, setFormConfigWithValue] = useState<
    Array<Array<IConfigWithValue>>
  >([[]]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isVibrate, setIsVibrate] = useState<
    | Array<{ fieldId: number; fieldLabel: string; max?: number } | undefined>
    | undefined
  >();
  const [isShowConfirmation, setIsShowConfirmation] = useState(false);

  const auth = useContext(AuthContext);

  const { loading, formDetail, quotation } = useSelector(
    (state: RootState) => state.quote
  );

  useEffect(() => {
    dispatch(getFormThunk());
  }, []);

  useEffect(() => {
    if (formDetail?.data?.formConfig)
      setFormConfigWithValue(formDetail.data.formConfig);
  }, [formDetail]);

  const valueChange = (fieldIndex: number, value?: string | number) => {
    setFormConfigWithValue((prev) => {
      const updatedConfig = prev.map((configs, pageIdx) => {
        if (pageIdx === pageIndex) {
          return configs.map((field, fieldIdx) => {
            if (fieldIdx === fieldIndex) {
              return {
                ...field,
                value,
              };
            }
            return field;
          });
        }
        return configs;
      });
      return updatedConfig;
    });
  };

  const fileChange = (fieldIndex: number, file: Array<File>) => {
    setFormConfigWithValue((prev) => {
      const updatedConfig = prev.map((configs, pageIdx) => {
        if (pageIdx === pageIndex) {
          return configs.map((field, fieldIdx) => {
            if (fieldIdx === fieldIndex) {
              return {
                ...field,
                file,
              };
            }
            return field;
          });
        }
        return configs;
      });
      return updatedConfig;
    });
  };

  const checkIsConditionMet = (fieldId: number) => {
    const field = formDetail?.data?.formConfig
      .flatMap((page) => page)
      .find((field) => field.fieldId === fieldId);
    const fieldCondition = field?.condition
      .filter((cond) => isNonEmptyString(cond.value) && isDefined(cond.fieldId))
      .map(
        (cond) => cond.fieldId && cond.value === getFieldValue(cond.fieldId)
      );

    return field?.conditionMethod === ConditionMethodEnum.Or
      ? fieldCondition?.some((value) => value)
      : fieldCondition?.every((value) => value);
  };

  const checkIsVerify = (): Array<
    | {
        fieldId: number;
        fieldLabel: string;
        max?: number;
      }
    | undefined
  > => {
    return formConfigWithValue[pageIndex]
      .filter((field) => checkIsConditionMet(field.fieldId))
      .map((form) => {
        const field = {
          fieldId: form.fieldId,
          fieldLabel: isNonEmptyString(form.fieldLabel[locale])
            ? form.fieldLabel[locale]
            : form.fieldLabel.en,
        };

        if (
          form.fieldType === FieldTypeEnum.Input &&
          isNonEmptyString(form.value)
        ) {
          const fieldSetting = form.fieldSetting as IInputFieldSetting;
          if (
            fieldSetting.chargeConditions &&
            fieldSetting.inputType === FieldInputTypeEnum.Number
          ) {
            const numberValue = parseFloat(form.value!.toString());

            return !isNonEmptyString(
              fieldSetting.chargeConditions[
                fieldSetting.chargeConditions.length - 1
              ].max
            ) ||
              fieldSetting.chargeConditions[
                fieldSetting.chargeConditions.length - 1
              ].max! > numberValue
              ? undefined
              : {
                  ...field,
                  max: fieldSetting.chargeConditions[
                    fieldSetting.chargeConditions.length - 1
                  ].max,
                };
          }
        }
        return (!form.optional && isNonEmptyString(form.value)) || form.optional
          ? undefined
          : field;
      })
      .filter((value) => value);
  };

  const getPage = (form: Array<IConfigWithValue>) => {
    return form.find((field) => field.fieldType === FieldTypeEnum.Page);
  };

  const getFieldValue = (fieldId: number) => {
    const formConfigPageMerge = formConfigWithValue.flatMap((config) => config);

    return formConfigPageMerge.find((field) => field.fieldId === fieldId)
      ?.value;
  };

  const getNextPageIndex = () => {
    const nextPage =
      (formDetail?.data?.formConfig.slice(pageIndex + 1).findIndex((form) => {
        const pageSetting = getPage(form);
        const condition = pageSetting?.condition;

        return condition
          ? pageSetting.conditionMethod === ConditionMethodEnum.Or
            ? condition
                ?.map(
                  (cond) =>
                    cond.fieldId && cond.value === getFieldValue(cond.fieldId)
                )
                .some((value) => value)
            : condition
                ?.map(
                  (cond) =>
                    cond.fieldId && cond.value === getFieldValue(cond.fieldId)
                )
                .every((value) => value)
          : true;
      }) ?? -1) + 1;

    if (nextPage > 0) {
      setPageBreadCrumb((prev) => [...prev, pageIndex]);
    }

    return pageIndex + nextPage;
  };

  const checkIsLastPage = () => {
    return (
      (formDetail?.data?.formConfig.slice(pageIndex + 1).findIndex((form) => {
        const pageSetting = getPage(form);
        const condition = pageSetting?.condition;

        return condition
          ? pageSetting.conditionMethod === ConditionMethodEnum.Or
            ? condition
                ?.map(
                  (cond) =>
                    cond.fieldId && cond.value === getFieldValue(cond.fieldId)
                )
                .some((value) => value)
            : condition
                ?.map(
                  (cond) =>
                    cond.fieldId && cond.value === getFieldValue(cond.fieldId)
                )
                .every((value) => value)
          : true;
      }) ?? -1) < 0
    );
  };

  useEffect(() => {
    if (searchParams.get("quoteId") && isQuoteOnly) {
      const quoteId = searchParams.get("quoteId")!.replaceAll(" ", "+");

      dispatch(getQuoteThunk({ quoteId }));

      return () => {
        dispatch(clearQuotation());
      };
    } else if (isQuoteOnly) {
      navigate("/");
    }
  }, [searchParams.get("quoteId"), isQuoteOnly]);

  const submitForm = () => {
    if (
      auth?.userDetails?.phoneNumber &&
      auth?.userDetails?.name &&
      auth?.userDetails?.email &&
      auth?.userDetails?.billingTo &&
      auth?.userDetails?.billingAddress &&
      auth?.userDetails?.siteAddress &&
      formDetail?.data?.id
    ) {
      const formData = new FormData();
      const quoteValue = formConfigWithValue
        .flatMap((form) => form)
        .filter((field) => isNonEmptyString(field.value) || field.file)
        .map((field, fieldIdx) => {
          if (field.file) {
            let fieldName: Array<string> = [];
            field.file.forEach((file) => {
              fieldName.push(`${file.name}.${file.type.split("/")[1]}`);
              formData.append("attachment[]", file);
            });
            return { fieldId: field.fieldId, value: fieldName.join(",") };
          }

          return {
            fieldId: field.fieldId,
            value: checkIsConditionMet(field.fieldId) ? field.value : undefined,
          };
        });

      formData.append("quoteValue", JSON.stringify(quoteValue));
      formData.append("formId", formDetail.data.id.toString());
      formData.append("phoneNumber", auth.userDetails.phoneNumber.toString());
      formData.append("name", auth.userDetails.name.toString());
      formData.append("email", auth.userDetails.email.toString());
      formData.append("siteAddress", auth.userDetails.siteAddress.toString());
      formData.append(
        "billingAddress",
        auth.userDetails.billingAddress.toString()
      );
      formData.append("billingTo", auth.userDetails.billingTo.toString());
      dispatch(formPostThunk(formData)).then((res) => {
        setIsShowConfirmation(false);
        if (res.payload.code === 200) {
          setIsSubmit(true);
          auth.handleQuoteNo(res.payload.quoteId);
          auth.handleInvoiceNo(res.payload.invoiceId);
        } else toast.error(intl.formatMessage({ id: "errorOccur" }));
      });
    }
  };

  useEffect(() => {
    if (isVibrate) {
      setTimeout(() => setIsVibrate(undefined), 700);
    }
  }, [isVibrate]);

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />
      <TermAndCondition setIsShowTnC={setIsShowTnC} isShowTnC={isShowTnC} />
      {searchParams.get("quoteId") ? (
        !quotation ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-50">
            <div className="w-10 h-10 border-4 border-blue-500 rounded-full border-t-transparent animate-spin" />
          </div>
        ) : quotation.data ? (
          <IntlProvider locale="en" messages={enMessages}>
            <QuotationPreview
              isShowInvoice={isShowInvoice}
              formConfigWithValue={quotation.data.quote}
              quotation={quotation}
              printError={() => {
                toast.error(intl.formatMessage({ id: "unableToPrint" }));
                globalLoadingDispatch(false);
              }}
            />
          </IntlProvider>
        ) : (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white bg-opacity-50">
            <img
              src="/image/logo.png"
              className="mb-4 cursor-pointer w-44"
              onClick={() => navigate("/")}
            />
            <div className="mb-10 text-xl">
              {intl.formatMessage({ id: "quotationInvoiceHaveExpired" })}
            </div>
            <button
              className="bg-[#6b5c45] border-2 border-[#3f3525] text-white px-8 py-2 rounded hover:bg-[#3f3525] transition font-semibold"
              onClick={() => navigate("/")}
            >
              {intl.formatMessage({
                id: "backToHome",
              })}
            </button>
          </div>
        )
      ) : isShowConfirmation ? (
        <div className="bg-[#c2b39d] flex items-stretch justify-center flex-col p-4 flex-grow">
          <div className="w-full mx-auto max-w-96">
            <div className="mb-6 text-3xl font-bold text-center text-white">
              <FormattedMessage id="confirmation" />
            </div>
            <div className="mb-6 text-xl text-center text-[#6e5f49]">
              <FormattedMessage id="confirmSubmitQuote" />
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                className="px-4 py-2 font-bold text-white transition-colors bg-gray-500 border-2 border-gray-700 rounded hover:bg-gray-700 focus:outline-none focus:shadow-outline"
                type="button"
                onClick={() => setIsShowConfirmation(false)}
              >
                <FormattedMessage id="back" />
              </button>
              <button
                className="bg-[#6b5c45] border-2 border-[#3f3525] text-white px-4 py-2 rounded hover:bg-[#3f3525] transition font-semibold"
                type="button"
                disabled={loading}
                onClick={submitForm}
              >
                <FormattedMessage id={loading ? "submitting" : "confirm"} />
              </button>
            </div>
          </div>
        </div>
      ) : isSubmit ? (
        <>
          {isShow ? (
            <IntlProvider locale="en" messages={enMessages}>
              <QuotationPreview
                isShowInvoice={isShowInvoice}
                formConfigWithValue={formConfigWithValue}
                printError={() => {
                  toast.error(intl.formatMessage({ id: "unableToPrint" }));
                  globalLoadingDispatch(false);
                }}
              />
            </IntlProvider>
          ) : (
            <CongratulationsPage setIsShow={setIsShow} />
          )}
        </>
      ) : formDetail?.data?.formConfig ? (
        <>
          <div className="bg-[#c2b39d] flex items-stretch justify-center flex-col p-4 flex-grow">
            {pageBreadrumb.length > 0 && (
              <IoArrowBack
                className="absolute cursor-pointer top-5 left-3"
                size={25}
                onClick={() => {
                  if (!loading) {
                    const lastPage = pageBreadrumb[pageBreadrumb.length - 1];
                    setPageIndex(lastPage);
                    setPageBreadCrumb((prev) =>
                      prev.filter((breadcrumb) => breadcrumb !== lastPage)
                    );
                  }
                }}
              />
            )}
            <div className="w-full mx-auto max-w-96">
              <h2 className="mb-6 text-2xl font-bold text-center text-white">
                <FormattedMessage id="requestQuotation" />
              </h2>
              {/* <div>
            <ProgressIndicator
              value={pageIndex}
              max={
                checkIsLastPage()
                  ? pageIndex
                  : formDetail.data.formConfig.length - 1
              }
            />
          </div> */}
              <div className="flex flex-col items-stretch gap-4">
                {formConfigWithValue[pageIndex]
                  .filter((config) => config.fieldType !== FieldTypeEnum.Page)
                  .map((config, index) => (
                    <React.Fragment key={index}>
                      {(config.condition.length <= 0 ||
                        (config.condition.length > 0 &&
                          checkIsConditionMet(config.fieldId))) && (
                        <div>
                          <div className="flex items-center gap-2">
                            <label
                              className="block text-[#6e5f49]"
                              htmlFor={config.fieldLabel.en.replaceAll(" ", "")}
                            >
                              {isNonEmptyString(config.fieldLabel[locale])
                                ? config.fieldLabel[locale]
                                : config.fieldLabel.en}{" "}
                              {!config.optional && (
                                <span className="text-red-900">*</span>
                              )}
                            </label>
                            {config.optional &&
                              config.fieldType === FieldTypeEnum.Dropdown && (
                                <CiEraser
                                  className="text-blue-600 cursor-pointer hover:text-blue-400"
                                  size={20}
                                  onClick={() => valueChange(index, undefined)}
                                />
                              )}
                          </div>
                          {config.fieldType === FieldTypeEnum.Input ? (
                            <PreviewInput
                              fieldSetting={
                                config.fieldSetting as IInputFieldSetting
                              }
                              fieldLabel={config.fieldLabel}
                              languagePreview={locale}
                              onChange={(value: string | number) =>
                                valueChange(index, value)
                              }
                              optional={config.optional}
                              isVibrate={
                                isVibrate?.find(
                                  (field) => field?.fieldId === config.fieldId
                                )
                                  ? true
                                  : false
                              }
                              value={config.value ?? ""}
                            />
                          ) : config.fieldType === FieldTypeEnum.Dropdown ? (
                            <PreviewDropdown
                              fieldSetting={
                                config.fieldSetting as Array<IDropdownSetting>
                              }
                              optional={config.optional}
                              fieldLabel={config.fieldLabel}
                              languagePreview={locale}
                              onChange={(value: string | number) =>
                                valueChange(index, value)
                              }
                              isVibrate={
                                isVibrate?.find(
                                  (field) => field?.fieldId === config.fieldId
                                )
                                  ? true
                                  : false
                              }
                              value={config.value ?? ""}
                            />
                          ) : (
                            <PreviewAttachment
                              fieldSetting={
                                config.fieldSetting as IAttachmentSetting
                              }
                              fieldLabel={config.fieldLabel}
                              optional={config.optional}
                              phone={auth?.userDetails?.phoneNumber}
                              file={config.file ?? []}
                              onChange={(value: Array<File>) =>
                                fileChange(index, value)
                              }
                              isVibrate={
                                isVibrate?.find(
                                  (field) => field?.fieldId === config.fieldId
                                )
                                  ? true
                                  : false
                              }
                            />
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                {checkIsLastPage() && (
                  <div className="flex">
                    <Checkbox onChange={setIsCheck} checked={isCheck} />
                    <div className="ms-2 me-1 text-[#6e5f49]">
                      {intl.formatMessage({ id: "agreeTo" })}
                    </div>
                    <div
                      className="text-blue-600 cursor-pointer hover:text-blue-700"
                      onClick={() => setIsShowTnC(true)}
                    >
                      {intl.formatMessage({ id: "termsNCondition" })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <button
              className="bg-[#6b5c45] border-2 border-[#3f3525] text-white px-4 py-2 rounded hover:bg-[#3f3525] transition font-semibold"
              type="button"
              disabled={loading}
              onClick={() => {
                const invalidField = checkIsVerify();
                if (invalidField.length <= 0) {
                  if (!checkIsLastPage()) setPageIndex(getNextPageIndex());
                  else {
                    if (isCheck) setIsShowConfirmation(true);
                    else toast.error(intl.formatMessage({ id: "warningTnC" }));
                  }
                } else {
                  const formRequired =
                    invalidField.filter((field) => !isDefined(field?.max))
                      .length > 0;
                  if (formRequired) {
                    toast.error(intl.formatMessage({ id: "formRequired" }));
                  }

                  invalidField
                    .filter((field) => isDefined(field?.max))
                    .forEach((field) => {
                      if (field) {
                        toast.error(
                          intl.formatMessage(
                            { id: "maxUserError" },
                            { N: field.fieldLabel, X: field.max }
                          )
                        );
                      }
                    });
                  setIsVibrate(invalidField);
                }
              }}
            >
              <FormattedMessage
                id={
                  !checkIsLastPage()
                    ? "next"
                    : loading
                    ? "submitting"
                    : "submit"
                }
              />
            </button>
          </div>
        </>
      ) : null}
    </>
  );
};
